
import { useState } from 'react'
import { createClient } from '@/lib/supabase-client'
import { type UserProfile } from '../types'

interface Props {
  profile: UserProfile
  onUpdate: () => void
}

export default function APIKeySection({ profile, onUpdate }: Props) {
  const [copied, setCopied] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [showKey, setShowKey] = useState(false)
  const supabase = createClient()

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profile.api_key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const regenerateKey = async () => {
    if (!confirm('Are you sure you want to regenerate your API key? This will invalidate your current key and you\'ll need to update it in all your integrations.')) {
      return
    }

    setRegenerating(true)
    const { data: { session } } = await supabase.auth.getSession()
    
    if (session) {
      // Generate new key (this would typically be done server-side)
      const newKey = 'atg_' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ api_key: newKey })
        .eq('id', session.user.id)

      if (!profileError) {
        // Also sync to api_keys table
        try {
          const { data: existingKeys } = await supabase
            .from('api_keys')
            .select('id, name')
            .eq('user_id', session.user.id)

          if (existingKeys && existingKeys.length > 0) {
            const defaultKey = existingKeys.find(k => k.name === 'Default API Key') || existingKeys[0]
            await supabase
              .from('api_keys')
              .update({ api_key: newKey })
              .eq('id', defaultKey.id)
          } else {
            await supabase
              .from('api_keys')
              .insert({
                user_id: session.user.id,
                api_key: newKey,
                name: 'Default API Key'
              })
          }
        } catch (syncError) {
          console.error('Error syncing regenerated key to api_keys table:', syncError)
        }

        onUpdate()
        alert('API key regenerated successfully!')
      } else {
        alert('Failed to regenerate API key. Please try again.')
      }
    }
    setRegenerating(false)
  }

  const maskedKey = profile.api_key.substring(0, 8) + '•'.repeat(56) + profile.api_key.substring(64)

  return (
    <div className="glass-card rounded-xl p-6 hover-lift">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          API Key
        </h3>
        <button
          onClick={() => setShowKey(!showKey)}
          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition"
        >
          {showKey ? 'Hide' : 'Show'}
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your API Key
          </label>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={showKey ? profile.api_key : maskedKey}
                readOnly
                className="w-full px-4 py-3 border border-gray-300/50 dark:border-slate-600/50 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm text-gray-900 dark:text-white font-mono text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {copied && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1 text-green-600 dark:text-green-400 animate-fade-in">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-medium">Copied!</span>
                </div>
              )}
            </div>
            <button
              onClick={copyToClipboard}
              className="px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 text-sm font-medium transition hover-lift flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Copy</span>
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-start">
            <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>Use this key to authenticate API requests. Keep it secure and never share it publicly.</span>
          </p>
        </div>

        <div className="pt-4 border-t border-gray-200/50 dark:border-slate-700/50">
          <button
            onClick={regenerateKey}
            disabled={regenerating}
            className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center space-x-2"
          >
            {regenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-red-600 dark:border-red-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Regenerating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Regenerate API Key</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
